import { Show } from "shared/components";
import { TruncateWithTooltip } from "shared/components/TruncateWithTooltip";
import moment from "moment";
import { Link } from "react-router-dom";
import { timeLineSection } from "../constants";
import { User } from "views/settings-users/types";
import { TaskCreatedBy, WorkflowCreator } from "../types";

const isWorkflowCreator = (
  createdBy: TaskCreatedBy | null | undefined
): createdBy is WorkflowCreator => {
  return createdBy !== null && createdBy !== undefined && "type" in createdBy;
};

type TimelineSectionProps = {
  createdAt?: string;
  updatedAt?: string | null;
  completedAt?: string | null;
  assignees: User[];
  completedBy?: User | null;
  createdBy?: TaskCreatedBy | null;
};

export const TimelineSection = ({
  createdAt,
  updatedAt,
  completedAt,
  completedBy,
  createdBy,
  assignees
}: TimelineSectionProps) => {
  const createdAtDisplay = createdAt
    ? moment(createdAt).format("MMM D, YYYY [at] h:mm A")
    : "-";
  const updatedAtDisplay =
    updatedAt && updatedAt !== "-"
      ? moment(updatedAt).format("MMM D, YYYY [at] h:mm A")
      : "-";
  const completedAtDisplay =
    completedAt && completedAt !== "-"
      ? moment(completedAt).format("MMM D, YYYY [at] h:mm A")
      : "-";
  const completedByName = completedBy?.id
    ? assignees.find((u: any) => u.id === completedBy.id)?.name || "-"
    : "-";

  const isWorkflow = isWorkflowCreator(createdBy);

  const getCreatedByDisplay = (): string => {
    if (isWorkflow) {
      if (createdBy.name) return `Workflow - ${createdBy.name}`;
      return "Workflow";
    }
    return createdBy?.name || "-";
  };

  const createdByDisplay = getCreatedByDisplay();

  const renderCreatedBy = () => {
    if (!createdBy || createdByDisplay === "-")
      return (
        <span className="text-sm font-medium text-content-1000 flex-1">-</span>
      );

    if (isWorkflow)
      return (
        <Link
          to={`/workflows/${createdBy.id}`}
          className="flex-1 min-w-0 block"
        >
          <TruncateWithTooltip
            content={createdByDisplay}
            className="text-sm font-medium text-content-1000 hover:underline cursor-pointer"
            tooltipClassName="z-[102] break-words whitespace-normal"
            placement="bottom"
          />
        </Link>
      );

    return (
      <TruncateWithTooltip
        content={createdByDisplay}
        className="text-sm font-medium text-content-1000 flex-1"
        tooltipClassName="z-[102] break-words whitespace-normal"
        placement="bottom"
      />
    );
  };

  return (
    <div className="space-y-2">
      {[
        [timeLineSection.updated_at, updatedAtDisplay],
        [timeLineSection.completed_at, completedAtDisplay],
        [timeLineSection.completed_by, completedByName],
        [timeLineSection.created_at, createdAtDisplay],
        [timeLineSection.created_by, createdByDisplay]
      ].map(([label, value]) => (
        <div key={label} className="flex items-center gap-2">
          <div className="min-w-[120px]">
            <span className="text-sm text-content-400">{label}</span>
          </div>
          <Show if={label === timeLineSection.created_by}>
            {renderCreatedBy()}
          </Show>
          <Show if={label !== timeLineSection.created_by}>
            <span className="text-sm font-medium text-content-1000 flex-1">
              {value && value !== "-" ? value : "-"}
            </span>
          </Show>
        </div>
      ))}
    </div>
  );
};
